import { Alert } from "react-bootstrap";
import DefaultSlide from "../../components/Slides/templates/DefaultSlide";
import ImageSlide from "../../components/Slides/templates/ImageSlide";
import TitleSlide from "../../components/Slides/templates/TitleSlide";
import StudentPicker from "../../components/global/StudentPicker";
import { ISlideObject } from "../../components/Slides/templates/ISlideObject";
import CodeSlide from "../../components/Slides/templates/CodeSlide";
import { useEffect, useRef } from "react";

interface ToastifyOptions {
  text: string;
  duration?: number;
  newWindow?: boolean;
  close?: boolean;
  gravity?: "top" | "bottom";
  position?: "left" | "center" | "right";
  stopOnFocus?: boolean;
  style?: Record<string, string>;
  onClick?: () => void;
}

declare function Toastify(options: ToastifyOptions): { showToast: () => void };

export default function S04P01({ reveal }: ISlideObject) {
  useEffect(() => {
    window.addEventListener("offline", () => {
      Toastify({
        text: "Parece que estás offline 😢",
        duration: 3000,
        newWindow: true,
        close: true,
        gravity: "top", // `top` or `bottom`
        position: "left", // `left`, `center` or `right`
        stopOnFocus: true, // Prevents dismissing of toast on hover
        style: {
          background: "linear-gradient(to right,rgb(176, 0, 67),rgb(57, 2, 2))",
        },
        onClick: function () {}, // Callback after click
      }).showToast();
    });
    window.addEventListener("online", () => {
      Toastify({
        text: "Vuelves a estar online 😃",
        duration: 3000,
        newWindow: true,
        close: true,
        gravity: "top", // `top` or `bottom`
        position: "left", // `left`, `center` or `right`
        stopOnFocus: true, // Prevents dismissing of toast on hover
        style: {
          background: "linear-gradient(to right, #00b09b, #96c93d)",
        },
        onClick: function () {}, // Callback after click
      }).showToast();
    });
  }, []);

  return (
    <>
      <TitleSlide
        title="TI8512 - Desarrollo Ágil de Aplicaciones Web"
        subtitle="Semana 04 Parte 1"
      />
      <DefaultSlide title="Agenda">
        <div>
          <ul>
            <li>Repaso</li>
            <li>Introducción a JavaScript</li>
            <li>NodeJS</li>
          </ul>
        </div>
      </DefaultSlide>

      <section>
        <ImageSlide image="/images/S04/summary.jpg" />
        <section data-background="#003400">
          <h1>¿Qué debemos saber hasta el momento?</h1>
        </section>
        <section>
          <p>
            En la clase anterior, revisamos los conceptos básicos de HTML y CSS.
            Aprendimos a crear una estructura básica de una página web y a
            estilizarla con CSS.
          </p>
          <p>
            También vimos cómo utilizar bibliotecas como bootstrap para agilizar
            el proceso de desarrollo.
          </p>
          <p className="fragment">
            Ya deberían de entender algunos memes de la programación
          </p>
        </section>
        <section>
          <img
            width={350}
            src="/images/S04/S04-joke.jpg"
            alt="Chiste sobre html y css"
          />
        </section>
        <section>
          <img src="/images/S04/css.jpg" alt="Chiste sobre html y css" />
        </section>
        <section>
          <img src="/images/S04/css2.jpg" alt="Chiste sobre html y css" />
        </section>
        <section>
          <p>
            A partir de mañana, tienen acceso a una grabación de ejemplo
            sobre el uso de bootstrap. Esta debe ser visualizada de forma
            asincrónica.
          </p>
          <p>
            Les va a servir con el laboratorio en caso de que estén teniendo
            inconvenientes.
          </p>
        </section>
        <section>
          <p>¿Cómo van con el laboratorio?</p>
          <p>Recordar que la fecha de entrega es este Domingo.</p>
        </section>
      </section>

      <section>
        <TitleSlide title="Introducción a JavaScript" />
        <section>
          <p>
            Algunos de los contenidos en esta presentación fueron tomados y
            adaptados de recursos preparados por el profesor Isaac Alpizar.
          </p>
        </section>
        <section data-background="#33003c">
          <img src="/images/S04/htmlcssjs.png" alt="Chiste sobre html y css" />
        </section>
        <section data-background="#33003c">
          <img src="/images/S02/htmlcss.png" alt="Chiste sobre html y css" />
        </section>
        <section
          data-background="#33003c"
          style={{ position: "relative", height: "400px" }}
        >
          <div style={{ position: "absolute", top: -100, left: 10 }}>
            Clicks: <span id="click-counter">0</span>
          </div>
          <button
            style={{ position: "absolute", top: -100, left: 200 }}
            id="toggle-animation"
            onClick={() => {
              const chicken = document.getElementById("chicken");
              if (chicken) {
                chicken.classList.toggle("moving-item");
              }
            }}
          >
            Toggle Animation
          </button>
          <button
            style={{ position: "absolute", top: -100, left: 500 }}
            id="reset-speed"
            onClick={() => {
              const counter = document.getElementById("click-counter");
              if (counter) {
                const currentCount = 0;
                counter.innerText = currentCount.toString();
              }
            }}
          >
            Reset Score
          </button>
          <span
            id="chicken"
            className="moving-item"
            onClick={() => {
              const counter = document.getElementById("click-counter");
              if (counter) {
                const currentCount = parseInt(counter.innerText);
                counter.innerText = (currentCount + 1).toString();
                const chicken = document.getElementById("chicken");
                if (chicken) {
                  const currentSpeed =
                    parseFloat(chicken.style.animationDuration) || 20;
                  if (currentCount > 19) return;
                  chicken.style.animationDuration = `${currentSpeed - currentCount}s`;
                }
              }
            }}
          >
            🐔
          </span>
        </section>
        <DefaultSlide title="¿Por qué JavaScript?">
          <div>
            <p>
              JavaScript se utiliza principalmente para crear interactividad en
              las páginas web, pero también se puede utilizar en el lado del
              servidor con Node.js.
            </p>
            <p>
              Es un lenguaje de scripting interpretado en tiempo de ejecución.
            </p>
            <p>Creado en 1995 por Brendan Eich.</p>
          </div>
        </DefaultSlide>

        <DefaultSlide title="¿Qué es JavaScript?">
          <div>
            <ul>
              <li>Soporta multiples paradigmas:</li>
              <ul>
                <li>Programación orientada a objetos</li>
                <li>Programación funcional</li>
                <li>Programación imperativa</li>
                <li>Programación basada en eventos</li>
                <li>Programación basada en prototipos</li>
              </ul>
              <li>Tipado débil</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="¿Qué es ECMAScript?">
          <div>
            <p>
              ECMAScript es una especificación de lenguaje de scripting
              estandarizada por Ecma International. Más información en{" "}
              <a
                href="https://262.ecma-international.org/12.0/"
                target="_blank"
                rel="noopener noreferrer"
              >
                este enlace
              </a>
              .
            </p>
            <p>
              JavaScript es una implementación de ECMAScript, al igual que otros
              lenguajes como JScript y ActionScript.
            </p>
            <p>
              La especificación define la sintaxis, tipos de datos, estructuras
              de control, objetos estándar y más.
            </p>
          </div>
        </DefaultSlide>
        <section>
          <p>
            ¿Recuerdan los motores web?{" "}
            <span className="fragment">
              Es hora de agregar un concepto nuevo: un motor de scripting.
            </span>
          </p>
        </section>
        <DefaultSlide title="Tipos de Script Engines">
          <div>
            <table>
              <thead>
                <tr>
                  <th>Engine</th>
                  <th>Desarrollado por</th>
                  <th>Utilizado en</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>V8</td>
                  <td>Google</td>
                  <td>Google Chrome, Node.js, Edge</td>
                </tr>
                <tr>
                  <td>SpiderMonkey</td>
                  <td>Mozilla</td>
                  <td>Firefox</td>
                </tr>
                <tr>
                  <td>Chakra</td>
                  <td>Microsoft</td>
                  <td>Microsoft Edge (antiguo)</td>
                </tr>
                <tr>
                  <td>JavaScriptCore</td>
                  <td>Apple</td>
                  <td>Safari</td>
                </tr>
              </tbody>
            </table>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Tipos de Script Engines">
          <div>
            <table>
              <thead>
                <tr>
                  <th>Engine</th>
                  <th>Desarrollado por</th>
                  <th>Utilizado en</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ backgroundColor: "#000" }}>
                  <td>V8</td>
                  <td>Google</td>
                  <td>Google Chrome, Node.js, Edge</td>
                </tr>
                <tr style={{ opacity: "0.3" }}>
                  <td>SpiderMonkey</td>
                  <td>Mozilla</td>
                  <td>Firefox</td>
                </tr>
                <tr style={{ opacity: "0.3" }}>
                  <td>Chakra</td>
                  <td>Microsoft</td>
                  <td>Microsoft Edge (antiguo)</td>
                </tr>
                <tr style={{ opacity: "0.3" }}>
                  <td>JavaScriptCore</td>
                  <td>Apple</td>
                  <td>Safari</td>
                </tr>
              </tbody>
            </table>
          </div>
        </DefaultSlide>
        <DefaultSlide title="¿Cómo agregar código JavaScript a nuestro sitio web?">
          <div>
            <p>
              Existen tres formas de agregar código JavaScript a nuestro sitio
              web:
            </p>
            <ul>
              <li>Inline</li>
              <li>Interno</li>
              <li>Externo</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Inline">
          <div>
            <p>
              El código JavaScript se incluye directamente en el atributo
              "onclick" de un elemento HTML.
            </p>
            <pre>
              <code>
                {`<button onclick="alert('Hola mundo!')">Click me!</button>`}
              </code>
            </pre>
            <button onClick={() => alert("Hola mundo!")}>Click me!</button>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Interno">
          <div>
            <p>
              El código JavaScript se incluye dentro de una etiqueta{" "}
              <code>&lt;script&gt;</code> en el encabezado o cuerpo del
              documento HTML.
            </p>
            <pre>
              <code className="language-html">
                {`<script>
  function showMessage() {
    alert('Hola mundo!');
  }
</script>
<button onclick="showMessage()">Click me!</button>`}
              </code>
            </pre>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Externo">
          <div>
            <p>
              El código JavaScript se incluye en un archivo externo con la
              extensión .js y se vincula al documento HTML mediante la etiqueta
              &nbsp;
              <code>&lt;script src="archivo.js"&gt;</code>.
            </p>
            <pre>
              <code>
                {`<script src="archivo.js"></script>
<button onclick="showMessage()">Click me!</button>`}
              </code>
            </pre>
            <p className="fragment">
              El archivo externo puede contener múltiples funciones y variables,
              lo que permite una mejor organización del código.
            </p>
            <p className="fragment">
              Además permite importar bibliotecas externas...
            </p>
          </div>
        </DefaultSlide>
        <section>
          <p>
            ¿Notaron el atributo <code>onclick</code>?
          </p>
          <pre className="fragment">
            <code>
              {`<button onclick="alert('Hola mundo!')">Click me!</button>`}
            </code>
          </pre>
          <p className="fragment">
            Los eventos permiten enlazar código de JavaScript con eventos
            generados por un actor del sitio web, ya sea el navegador, el
            usuario, otro proceso externo, entre otros.
          </p>
          <p className="fragment">¿Pero qué son eventos de JS?</p>
        </section>
      </section>
      <section>
        <TitleSlide title="Eventos de JavaScript">
          <a
            href="https://developer.mozilla.org/en-US/docs/Web/Events"
            target="_blank"
            rel="noopener noreferrer"
          >
            {" "}
            Link a MDN
          </a>
        </TitleSlide>
        <DefaultSlide title="¿Qué son los eventos de JavaScript?">
          <div>
            <p>
              Los eventos de JavaScript son acciones o sucesos que ocurren en el
              navegador y que pueden ser detectados y manejados por el código
              JavaScript.
            </p>
            <p>
              Los eventos pueden ser generados por el usuario (como hacer clic
              en un botón, mover el mouse, presionar una tecla) o por el propio
              navegador (como cargar una página, cambiar el tamaño de la
              ventana, etc.).
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="¿Qué son los eventos de JavaScript?">
          <div>
            <p>
              JavaScript permite escuchar y responder a estos eventos mediante
              el uso de "event listeners" o "manejadores de eventos".
            </p>
            <p>
              Los eventos son una parte fundamental de la programación web, ya
              que permiten crear aplicaciones interactivas y dinámicas.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Tipos de eventos">
          <div>
            <p>Existen diferentes tipos de eventos en JavaScript:</p>
            <ul>
              <li>Eventos del mouse</li>
              <li>Eventos del teclado</li>
              <li>Eventos de formulario</li>
              <li>Eventos de carga del navegador</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Ejemplos de eventos">
          <div>
            <table className="table">
              <thead>
                <tr>
                  <th>Evento</th>
                  <th>Descripción</th>
                  <th>Ejemplo HTML</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>click</td>
                  <td>Ocurre cuando el usuario hace clic en un elemento.</td>
                  <td>
                    <code>
                      &lt;button onclick="alert('Clicked!')"&gt;Click
                      me&lt;/button&gt;
                    </code>
                  </td>
                </tr>
                <tr>
                  <td>keydown</td>
                  <td>
                    Ocurre cuando el usuario presiona una tecla mientras está
                    enfocado en un elemento.
                  </td>
                  <td>
                    <code>
                      &lt;input onkeydown="alert('Key Down!')"
                      placeholder="Press a key"/&gt;
                    </code>
                  </td>
                </tr>
                <tr>
                  <td>submit</td>
                  <td>Ocurre cuando el usuario envía un formulario.</td>
                  <td>
                    <code>
                      &lt;form onsubmit="alert('Form Submitted!')"&gt; &lt;input
                      type="text" /&gt; &lt;button
                      type="submit"&gt;Submit&lt;/button&gt; &lt;/form&gt;
                    </code>
                  </td>
                </tr>
                <tr>
                  <td>load</td>
                  <td>Ocurre cuando la página web ha terminado de cargarse.</td>
                  <td>
                    <code>
                      &lt;body onload="alert('Page Loaded!')"&gt;&lt;/body&gt;
                    </code>
                  </td>
                </tr>
                <tr>
                  <td>online</td>
                  <td>Ocurre cuando el navegador gana conexión de red.</td>
                  <td>
                    <code>
                      &lt;body ononline="alert('You are
                      online!')"&gt;&lt;/body&gt;
                    </code>
                  </td>
                </tr>
                <tr>
                  <td>offline</td>
                  <td>Ocurre cuando el navegador pierde conexión de red.</td>
                  <td>
                    <code>
                      &lt;body onoffline="alert('You are
                      offline!')"&gt;&lt;/body&gt;
                    </code>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </DefaultSlide>
        <section>
          <p>
            Volveremos a este tema más adelante, pero por ahora es importante
            que tengan en cuenta que los eventos son una parte fundamental de la
            programación web. Permiten crear aplicaciones interactivas y
            dinámicas, y son esenciales para la experiencia del usuario.
          </p>
          <p className="fragment">
            Notaron el alert en el atributo onClick <br />
            <code>
              &lt;button onclick="alert('Clicked!')"&gt;Click me&lt;/button&gt;
            </code>
          </p>
        </section>
      </section>
      <section>
        <TitleSlide title="Fundamentos de JavaScript" />
        <section>
          <p>
            Para empezar, vamos a pensar en JS como un loop infinito que se
            ejecuta en nuestro navegador. En ese loop se pueden declarar
            funciones, variables, definir eventos, entre otros.{" "}
            <span className="fragment">
              Esta no es una definición correcta, pero nos va a permitir
              aprender sobre JS más rápido.
            </span>
          </p>
        </section>
        <DefaultSlide title="Variables">
          <div>
            <p>
              Las variables son contenedores que almacenan datos. En JavaScript,
              las variables se pueden declarar utilizando las palabras clave{" "}
              <code>var</code>, <code>let</code> o <code>const</code>.
            </p>
            <p>
              La diferencia entre estas palabras clave radica en el alcance y la
              mutabilidad de la variable.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Ámbito de las variables">
          <div>
            <p>
              El ámbito de una variable se refiere a la parte del código donde
              la variable es accesible.
            </p>
            <p>En JavaScript, existen tres tipos de ámbito:</p>
            <ul>
              <li>Global</li>
              <li>Local</li>
              <li>Bloque</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Ámbito de las variables">
          <div>
            <table className="table">
              <thead>
                <tr>
                  <th>Ámbito</th>
                  <th>Descripción</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Global</td>
                  <td>
                    Las variables declaradas fuera de cualquier función tienen
                    ámbito global y son accesibles desde cualquier parte del
                    código.
                  </td>
                </tr>
                <tr>
                  <td>Local o de función</td>
                  <td>
                    Las variables declaradas dentro de una función tienen ámbito
                    local y son accesibles solo dentro de esa función.
                  </td>
                </tr>
                <tr>
                  <td>Ámbito de bloque</td>
                  <td>
                    Las variables declaradas dentro de un bloque (por ejemplo,
                    dentro de un bucle o una declaración <code>if</code>) tienen
                    ámbito de bloque y son accesibles solo dentro de ese bloque.
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="fragment">
              <code>var</code> tiene un ámbito de función, mientras que{" "}
              <code>let</code> y <code>const</code> tienen un ámbito de bloque.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Variables">
          <div>
            <table className="table">
              <thead>
                <tr>
                  <th>Palabra clave</th>
                  <th>Descripción</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <code>var</code>
                  </td>
                  <td>
                    Se utiliza para declarar variables de ámbito global o de
                    función. Las variables declaradas con <code>var</code>{" "}
                    pueden ser reasignadas y re-declaradas.
                  </td>
                </tr>
                <tr>
                  <td>
                    <code>let</code>
                  </td>
                  <td>
                    Se utiliza para declarar variables de ámbito de bloque. Las
                    variables declaradas con <code>let</code> pueden ser
                    reasignadas, pero no re-declaradas en el mismo ámbito.
                  </td>
                </tr>
                <tr>
                  <td>
                    <code>const</code>
                  </td>
                  <td>
                    Se utiliza para declarar variables de ámbito de bloque que
                    no pueden ser reasignadas ni re-declaradas. Sin embargo, los
                    objetos y arreglos declarados con <code>const</code> pueden
                    ser modificados.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </DefaultSlide>
        <section>
          <p>
            Vamos a introducir funciones para explicar con más detalle estos
            conceptos.
          </p>
        </section>
        <DefaultSlide title="Funciones">
          <div>
            <p>
              Las funciones son bloques de código re-utilizables que realizan una
              tarea específica. En JavaScript, las funciones se pueden declarar
              de varias maneras:
            </p>
            <ul>
              <li>Declaración de función</li>
              <li>Expresión de función (soon)</li>
              <li>Función de flecha (soon)</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Funciones">
          <div style={{ fontSize: "0.75em" }}>
            <p>La sintaxis para declarar una función es la siguiente:</p>
            <pre>
              <code>
                {`function sumar(a, b) {
  return a + b;
}`}
              </code>
            </pre>
            <p>
              Para llamar a una función, se utiliza su nombre seguido de
              paréntesis con los argumentos necesarios:
            </p>
            <pre>
              <code>
                {`nombreFuncion(argumento1, argumento2);
console.log(sumar(2, 3)); // Imprime 5`}
              </code>
            </pre>
            <p>
              En este caso, la función <code>sumar</code> recibe dos parámetros{" "}
              <code>a</code> y <code>b</code>, y devuelve la suma de ambos.
              Luego, se llama a la función con los argumentos <code>2</code> y{" "}
              <code>3</code>, lo que imprime <code>5</code> en la consola.
            </p>
          </div>
        </DefaultSlide>
        <section>
          <p>
            Vamos a abrir la consola del desarrollador y jugar con JavaScript un
            rato.
          </p>
          <p>
            Pero antes, debemos conocer sobre como imprimir valores en la
            ventana y en la consola.
          </p>
        </section>
        <DefaultSlide title="Imprimir valores en la consola">
          <div>
            <p>
              Para imprimir valores en la consola, se utiliza el método{" "}
              <code>console.log()</code>. Este método acepta un argumento y lo
              imprime en la consola del navegador.
            </p>
            <pre>
              <code>
                {`console.log('Hola mundo!'); // Imprime 'Hola mundo!'`}
              </code>
            </pre>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Imprimir valores en ventana">
          <div>
            <p>
              Para imprimir valores en la ventana del navegador, se utiliza el
              método <code>alert()</code>. Este método acepta un argumento y
              muestra una ventana emergente con el valor.
            </p>
            <pre>
              <code>
                {`alert('Hola mundo!'); // Muestra una ventana emergente con 'Hola mundo!'`}
              </code>
            </pre>
            <p>
              No utilizar este método para depurar código, ya que puede
              interrumpir el flujo de ejecución del programa. En su lugar,
              utilizar <code>console.log()</code> para imprimir valores en la
              consola.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="¿Qué es la consola?">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              La consola es una herramienta de desarrollo que permite
              interactuar con el navegador y depurar código JavaScript.
            </p>
            <p>
              Se puede acceder a la consola en la mayoría de los navegadores
              presionando <code>F12</code> o haciendo clic derecho en la página
              y seleccionando "Inspeccionar" o "Inspeccionar elemento".
            </p>
            <p>
              En la consola, se pueden ejecutar comandos de JavaScript, ver
              errores y advertencias, y depurar código.
            </p>
            <p>
              La consola también permite ver el DOM (Document Object Model) de
              la página, lo que facilita la manipulación de elementos HTML y
              CSS.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="El objeto window y console">
          <div>
            <p>
              El objeto <code id="miId">window</code> es el objeto global en el navegador
              y representa la ventana del navegador. Todos los objetos globales
              y funciones de JavaScript son propiedades y métodos del objeto{" "}
              <code>window</code>.
            </p>
            <p>
              El objeto <code>console</code> es una propiedad del objeto{" "}
              <code>window</code> y proporciona acceso a la consola del
              navegador. Permite imprimir mensajes, errores y advertencias en la
              consola.
            </p>
          </div>
        </DefaultSlide>
        <section>
          <p>Listo... A la consola!</p>
        </section>
        <CodeSlide title="Veamos un ejemplo" language="javascript">
          {`myVar = 1;
// ...
var myVar;`}
        </CodeSlide>
        <DefaultSlide title="Hoisting">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              El hoisting es un comportamiento de JavaScript en el que las
              declaraciones de variables y funciones se mueven a la parte
              superior de su ámbito antes de que se ejecute el código.
            </p>
            <p>
              Esto significa que se puede utilizar una variable o función antes
              de declararla en el código.
            </p>
            <p>
              Sin embargo, solo las declaraciones se mueven, no las
              inicializaciones. Por lo tanto, si se intenta acceder a una
              variable antes de declararla, su valor será <code>undefined</code>
              .
            </p>
          </div>
        </DefaultSlide>
        <section>
          <img
            width={700}
            src="/images/S04/jshoisting.webp"
            alt="JS hoisting meme"
          />
        </section>
        <section>
          <p>
            Hoisting es el origen de MUCHOS problemas en JS. Al no entender este
            concepto se pueden dar muchos problemas de ejecución.
          </p>
          <p className="fragment">
            ¿Solución? Declarar todas las variables al comienzo de un bloque.
          </p>
          <p className="fragment">
            Hablemos un poco más sobre declaración de variables
          </p>
        </section>
        <DefaultSlide title="Identificadores de variables">
          <div>
            <p>
              Los identificadores de variables son los nombres que se les
              asignan a las variables. En JavaScript, los identificadores deben
              seguir ciertas reglas:
            </p>
            <ul>
              <li>
                Deben comenzar con una letra, un guion bajo (_) o un signo de
                dólar ($).
              </li>
              <li>
                Pueden contener letras, números, guiones bajos y signos de
                dólar.
              </li>
              <li>No pueden contener espacios ni caracteres especiales.</li>
            </ul>
          </div>
        </DefaultSlide>

        <DefaultSlide title="Identificadores de variables">
          <div>
            <ul>
              <li>No pueden comenzar con un número.</li>
              <li>
                No pueden ser palabras reservadas de JavaScript (como{" "}
                <code>if</code>, <code>else</code>, <code>for</code>, etc.).
              </li>
            </ul>
            <p>
              Es recomendable utilizar nombres descriptivos para las variables ,
              de modo que el código sea más legible y fácil de entender.
            </p>
          </div>
        </DefaultSlide>

        <DefaultSlide title="Identificadores de variables">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              Por ejemplo, en lugar de usar <code>x</code> o <code>y</code>, es
              mejor usar nombres como <code>edad</code> o <code>nombre</code>.
            </p>
            <p>
              También es recomendable utilizar la convención de nomenclatura
              camelCase para los identificadores de variables, es decir, la
              primera palabra en minúscula y las siguientes palabras con la
              primera letra en mayúscula. Por ejemplo:{" "}
              <code>nombreCompleto</code>.
            </p>
            <p>
              En resumen, los identificadores de variables son los nombres que
              se les asignan a las variables y deben seguir ciertas reglas para
              ser válidos. Es recomendable utilizar nombres descriptivos y la
              convención de nomenclatura camelCase para mejorar la legibilidad
              del código.
            </p>
          </div>
        </DefaultSlide>

        <DefaultSlide title="Tipado dinámico">
          <div>
            <p>
              JavaScript es un lenguaje de programación dinámico y débilmente
              tipado, lo que significa que no es necesario declarar el tipo de
              dato de una variable al momento de crearla. El tipo de dato se
              determina automáticamente en tiempo de ejecución.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Tipado dinámico">
          <div>
            <p>
              Por ejemplo, se puede declarar una variable y asignarle un valor
              numérico, y luego re-asignarle un valor de tipo cadena sin ningún
              problema:
            </p>
            <pre>
              <code>
                {`let x = 5;
x = "Hola mundo!";`}
              </code>
            </pre>
            <p>
              En este caso, la variable <code>x</code> se declara como un número
              y luego se le asigna un valor de tipo cadena. JavaScript permite
              este tipo de operaciones sin necesidad de declarar el tipo de dato
              de la variable.
            </p>
          </div>
        </DefaultSlide>
        <section>
          <img src="/images/S04/dynamicmeme.jpg" alt="Bob esponja meme" />
          <p>
            Sin embargo, esto puede llevar a errores difíciles de detectar si no
            se tiene cuidado. Por lo tanto, es recomendable utilizar buenas
            prácticas de programación y evitar que variables cambien de tipo.{" "}
            <span className="fragment">
              En la clase de hoy, no se preocupen por esto, pero en el futuro
              será importante.
            </span>
          </p>
        </section>

        <ImageSlide image="/images/S04/soon.jpg"></ImageSlide>

        <DefaultSlide title="Tipos de datos">
          <div>
            <p>
              JavaScript tiene varios tipos de datos, que se pueden clasificar
              en dos categorías.
            </p>
            <ul>
              <li>tipos primitivos</li>
              <li>tipos de referencia</li>
            </ul>
            <p>
              Los tipos primitivos son aquellos que representan un solo valor,
              mientras que los tipos de referencia son aquellos que representan
              una colección de valores o un objeto.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Tipos de datos primitivos">
          <div>
            <table className="table">
              <thead>
                <tr>
                  <th>Tipo de dato</th>
                  <th>Descripción</th>
                  <th>Ejemplo</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>string</td>
                  <td>Representa una cadena de texto.</td>
                  <td>
                    <code>let saludo = "Hola mundo";</code>
                  </td>
                </tr>
                <tr>
                  <td>number</td>
                  <td>Representa un número (entero o decimal).</td>
                  <td>
                    <code>let numero = 42;</code>
                  </td>
                </tr>
                <tr>
                  <td>boolean</td>
                  <td>Representa un valor verdadero o falso.</td>
                  <td>
                    <code>let esVerdadero = true;</code>
                  </td>
                </tr>
                <tr>
                  <td>null</td>
                  <td>Representa la ausencia intencional de valor.</td>
                  <td>
                    <code>let valorNulo = null;</code>
                  </td>
                </tr>
                <tr>
                  <td>undefined</td>
                  <td>
                    Representa una variable que ha sido declarada pero no
                    inicializada.
                  </td>
                  <td>
                    <code>let valorIndefinido;</code>
                  </td>
                </tr>
                <tr>
                  <td>object</td>
                  <td>Representa una colección de pares clave-valor.</td>
                  <td>
                    <code>let objeto = {`{ clave: "valor" }`};</code>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Tipos de datos">
          <div>
            <p>
              Los tipos primitivos son inmutables, lo que significa que no se
              pueden cambiar una vez que se han creado. Por ejemplo, si se
              declara una variable de tipo cadena y se intenta cambiar su valor,
              se creará una nueva cadena en lugar de modificar la original:
            </p>
            <pre>
              <code>
                {`let str = "Hola";
str[0] = "h"; // No se puede cambiar el primer carácter
console.log(str); // Imprime "Hola"
str = "hola"; // Se crea una nueva cadena
console.log(str); // Imprime "hola"`}
              </code>
            </pre>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Tipos de datos de referencia">
          <div>
            <table className="table">
              <thead>
                <tr>
                  <th>Tipo de dato</th>
                  <th>Descripción</th>
                  <th>Ejemplo</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>array</td>
                  <td>Representa una colección ordenada de valores.</td>
                  <td>
                    <code>let arr = [1, 2, 3];</code>
                  </td>
                </tr>
                <tr>
                  <td>function</td>
                  <td>Representa un bloque de código re-utilizable.</td>
                  <td>
                    <code>{`function saludar() { alert('Hola'); `}</code>
                  </td>
                </tr>
                <tr>
                  <td>date</td>
                  <td>Representa una fecha y hora.</td>
                  <td>
                    <code>let fecha = new Date();</code>
                  </td>
                </tr>
                <tr>
                  <td>regexp</td>
                  <td>Representa una expresión regular.</td>
                  <td>
                    <code>let regex = /abc/;</code>
                  </td>
                </tr>
                <tr>
                  <td>symbol</td>
                  <td>Representa un valor único e inmutable.</td>
                  <td>
                    <code>let sym = Symbol('id');</code>
                  </td>
                </tr>
                <tr>
                  <td>bigint</td>
                  <td>Representa un número entero de gran tamaño.</td>
                  <td>
                    <code>let bigInt = 12345678901234567890n;</code>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Tipos de datos de referencia">
          <div>
            <p>
              Los tipos de referencia son mutables, lo que significa que se
              pueden cambiar una vez que se han creado. Por ejemplo, si se
              declara un objeto y se intenta cambiar su valor, se modificará el
              objeto original:
            </p>
            <pre>
              <code>
                {`let obj = { nombre: "Juan" };
obj.nombre = "Pedro"; // Se modifica el objeto original
console.log(obj.nombre); // Imprime "Pedro"
obj = { nombre: "Maria" }; // Se crea un nuevo objeto
console.log(obj.nombre); // Imprime "Maria"`}
              </code>
            </pre>
          </div>
        </DefaultSlide>
      </section>
      <section>
        <TitleSlide title="El tipo de dato: Object y Array" />
        <section>
          <p>
            El tipo de dato <code>object</code> es uno de los tipos de datos más
            importantes en JavaScript. Permite almacenar colecciones de datos y
            entidades complejas.
          </p>
          <p>
            Un objeto es una colección de propiedades, donde cada propiedad
            consiste en un nombre (clave) y un valor. Los valores pueden ser de
            cualquier tipo de dato, incluidos otros objetos.
          </p>
        </section>
        <DefaultSlide title="El tipo de dato: Object">
          <div>
            <p>La sintaxis para crear un objeto es la siguiente:</p>
            <pre>
              <code>
                {`let persona = {
  nombre: "Juan",
  edad: 30,
  ciudad: "Madrid"
};`}
              </code>
            </pre>
            <p>
              En este caso, se crea un objeto llamado <code>persona</code> con
              tres propiedades: <code>nombre</code>, <code>edad</code> y{" "}
              <code>ciudad</code>.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="El tipo de dato: Object">
          <div>
            <p>
              Para acceder a las propiedades de un objeto, se utiliza la
              notación de punto o la notación de corchetes:
            </p>
            <pre>
              <code>
                {`console.log(persona.nombre); // Imprime "Juan"
console.log(persona["edad"]); // Imprime 30`}
              </code>
            </pre>
            <p>
              En este caso, se accede a las propiedades <code>nombre</code> y{" "}
              <code>edad</code> del objeto <code>persona</code>.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="El tipo de dato: Object">
          <div>
            <p>
              También se pueden agregar, modificar o eliminar propiedades de un
              objeto:
            </p>
            <pre>
              <code>
                {`persona.pais = "España"; // Agrega una nueva propiedad
persona.edad = 31; // Modifica la propiedad edad
delete persona.ciudad; // Elimina la propiedad ciudad`}
              </code>
            </pre>
            <p>
              En este caso, se agrega una nueva propiedad <code>pais</code>, se
              modifica la propiedad <code>edad</code> y se elimina la propiedad{" "}
              <code>ciudad</code>.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="El tipo de dato: Object">
          <div>
            <p>
              Los objetos también pueden contener métodos, que son funciones
              asociadas a un objeto:
            </p>
            <pre>
              <code>
                {`let persona = {
  nombre: "Juan",
  edad: 30,
  saludar: function() {
    console.log("Hola, soy " + this.nombre);
  }
};
persona.saludar(); // Imprime "Hola, soy Juan"`}
              </code>
            </pre>
            <p>
              En este caso, se crea un método <code>saludar</code> que imprime
              un saludo utilizando la propiedad <code>nombre</code> del objeto
              <code>persona</code>.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="El tipo de dato: Array">
          <div>
            <p>
              El tipo de dato <code>array</code> es un tipo de dato especial en
              JavaScript que permite almacenar colecciones de datos ordenadas.
            </p>
            <p>
              Un array es un objeto con propiedades numeradas que representan
              los índices de los elementos del array. Los elementos pueden ser
              de cualquier tipo de dato, incluidos otros arrays y objetos.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="El tipo de dato: Array">
          <div>
            <p>La sintaxis para crear un array es la siguiente:</p>
            <pre>
              <code>{`let numeros = [1, 2, 3, 4, 5];`}</code>
            </pre>
            <p>
              En este caso, se crea un array llamado <code>numeros</code> que
              contiene cinco elementos.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="El tipo de dato: Array">
          <div>
            <p>
              Para acceder a los elementos de un array, se utiliza la notación
              de corchetes con el índice del elemento:
            </p>
            <pre>
              <code>
                {`console.log(numeros[0]); // Imprime 1
console.log(numeros[2]); // Imprime 3`}
              </code>
            </pre>
            <p>
              En este caso, se accede a los elementos en las posiciones{" "}
              <code>0</code> y <code>2</code> del array <code>numeros</code>.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="El tipo de dato: Array">
          <div>
            <p>
              También se pueden agregar, modificar o eliminar elementos de un
              array:
            </p>
            <pre>
              <code>
                {`numeros.push(6); // Agrega un nuevo elemento al final
numeros[0] = 0; // Modifica el primer elemento
numeros.splice(2, 1); // Elimina el tercer elemento`}
              </code>
            </pre>
            <p>
              En este caso, se agrega un nuevo elemento al final del array, se
              modifica el primer elemento y se elimina el tercer elemento.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="El tipo de dato: Array">
          <div>
            <p>
              Los arrays también tienen métodos incorporados que permiten
              realizar operaciones comunes, como ordenar, filtrar y mapear:
            </p>
            <pre>
              <code>
                {`let numeros = [5, 3, 8, 1, 4];
numeros.sort(); // Ordena el array
numeros.filter(num => num > 4); // Filtra los elementos mayores a 4
numeros.map(num => num * 2); // Multiplica cada elemento por 2`}
              </code>
            </pre>
            <p>
              En este caso, se ordena el array, se filtran los elementos mayores
              a <code>4</code> y se multiplica cada elemento por <code>2</code>.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="No confundir con Orientación a Objetos">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              Aunque JavaScript utiliza objetos, no debe confundirse con la
              programación orientada a objetos (OOP) tradicional.
            </p>
            <p>
              JavaScript es un lenguaje basado en prototipos, lo que significa
              que la herencia se realiza a través de prototipos y no de clases.
            </p>
            <p>
              Sin embargo, a partir de ECMAScript 6 (ES6), se introdujeron las
              clases como una forma de facilitar la escritura de código
              orientado a objetos, aunque internamente siguen utilizando
              prototipos.
            </p>
          </div>
        </DefaultSlide>
      </section>
      <section>
        <TitleSlide title="Operadores en JavaScript" />
        <DefaultSlide title="Operadores en JavaScript">
          <div>
            <p>
              Los operadores son símbolos que permiten realizar operaciones
              sobre variables y valores. En JavaScript, existen varios tipos de
              operadores:
            </p>
            <ul>
              <li>Operadores aritméticos</li>
              <li>Operadores de comparación</li>
              <li>Operadores lógicos</li>
              <li>Operadores de asignación</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Operadores aritméticos">
          <div>
            <p>
              Los operadores aritméticos permiten realizar operaciones
              matemáticas básicas:
            </p>
            <table className="table">
              <thead>
                <tr>
                  <th>Operador</th>
                  <th>Descripción</th>
                  <th>Ejemplo</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>+</td>
                  <td>Suma</td>
                  <td>
                    <code>5 + 3</code>
                  </td>
                </tr>
                <tr>
                  <td>-</td>
                  <td>Resta</td>
                  <td>
                    <code>5 - 3</code>
                  </td>
                </tr>
                <tr>
                  <td>*</td>
                  <td>Multiplicación</td>
                  <td>
                    <code>5 * 3</code>
                  </td>
                </tr>
                <tr>
                  <td>/</td>
                  <td>División</td>
                  <td>
                    <code>5 / 3</code>
                  </td>
                </tr>
                <tr>
                  <td>%</td>
                  <td>Módulo (resto de la división)</td>
                  <td>
                    <code>5 % 3</code>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Operadores de comparación">
          <div>
            <p style={{ marginTop: -20 }}>
              Los operadores de comparación permiten comparar dos valores y
              devolver un valor booleano (verdadero o falso):
            </p>
            <table className="table">
              <thead>
                <tr>
                  <th>Operador</th>
                  <th>Descripción</th>
                  <th>Ejemplo</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>==</td>
                  <td>Igualdad (no estricta)</td>
                  <td>
                    <code>5 == "5"</code>
                  </td>
                </tr>
                <tr>
                  <td>===</td>
                  <td>Igualdad (estricta)</td>
                  <td>
                    <code>5 === "5"</code>
                  </td>
                </tr>
                <tr>
                  <td>!=</td>
                  <td>Diferencia (no estricta)</td>
                  <td>
                    <code>5 != "5"</code>
                  </td>
                </tr>
                <tr>
                  <td>!==</td>
                  <td>Diferencia (estricta)</td>
                  <td>
                    <code>5 !== "5"</code>
                  </td>
                </tr>
                <tr>
                  <td>&gt;</td>
                  <td>Mayor que</td>
                  <td>
                    <code>5 &gt; 3</code>
                  </td>
                </tr>
                <tr>
                  <td>&lt;</td>
                  <td>Menor que</td>
                  <td>
                    <code>5 &lt; 3</code>
                  </td>
                </tr>
                <tr>
                  <td>&gt;=</td>
                  <td>Mayor o igual que</td>
                  <td>
                    <code>5 &gt;= 3</code>
                  </td>
                </tr>
                <tr>
                  <td>&lt;=</td>
                  <td>Menor o igual que</td>
                  <td>
                    <code>5 &lt;= 3</code>
                  </td>
                </tr>
                <tr>
                  <td>??</td>
                  <td>Nullish coalescing operator</td>
                  <td>
                    <code>let x = null ?? "default";</code>
                  </td>
                </tr>
                <tr>
                  <td>??=</td>
                  <td>Nullish coalescing assignment operator</td>
                  <td>
                    <code>x ??= "default";</code>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Operadores lógicos">
          <div>
            <p>
              Los operadores lógicos permiten combinar valores booleanos y
              devolver un valor booleano:
            </p>
            <table className="table">
              <thead>
                <tr>
                  <th>Operador</th>
                  <th>Descripción</th>
                  <th>Ejemplo</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>&&</td>
                  <td>AND (y)</td>
                  <td>
                    <code>true && false</code>
                  </td>
                </tr>
                <tr>
                  <td>||</td>
                  <td>OR (o)</td>
                  <td>
                    <code>true || false</code>
                  </td>
                </tr>
                <tr>
                  <td>!</td>
                  <td>NOT (no)</td>
                  <td>
                    <code>!true</code>
                  </td>
                </tr>
              </tbody>
            </table>
            <p>
              Estos operadores se utilizan comúnmente en condiciones y bucles
              para controlar el flujo de ejecución del código.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Operadores de asignación">
          <div>
            <p>
              Los operadores de asignación permiten asignar valores a variables:
            </p>
            <table className="table">
              <thead>
                <tr>
                  <th>Operador</th>
                  <th>Descripción</th>
                  <th>Ejemplo</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>=</td>
                  <td>Asignación simple</td>
                  <td>
                    <code>x = 5</code>
                  </td>
                </tr>
                <tr>
                  <td>+=</td>
                  <td>Asignación de suma</td>
                  <td>
                    <code>x += 5</code>
                  </td>
                </tr>
                <tr>
                  <td>-=</td>
                  <td>Asignación de resta</td>
                  <td>
                    <code>x -= 5</code>
                  </td>
                </tr>
                <tr>
                  <td>*=</td>
                  <td>Asignación de multiplicación</td>
                  <td>
                    <code>x *= 5</code>
                  </td>
                </tr>
                <tr>
                  <td>/=</td>
                  <td>Asignación de división</td>
                  <td>
                    <code>x /= 5</code>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </DefaultSlide>
      </section>
      <section>
        <TitleSlide title="Otras operaciones de JS" />
        <section>
          <ul>
            <li>If...Else</li>
            <li>Operador ternario</li>
            <li>Switch</li>
            <li>While</li>
            <li>Do..While</li>
            <li>For</li>
            <li>For..in</li>
            <li>For..of</li>
            <li>Try..catch</li>
          </ul>
        </section>
        <DefaultSlide title="If...Else">
          <div>
            <p style={{ marginTop: -20 }}>
              La declaración <code>if</code> se utiliza para ejecutar un bloque
              de código si se cumple una condición:
            </p>
            <pre>
              <code>
                {`if (condicion) {
  // Código a ejecutar si la condición es verdadera
}`}
              </code>
            </pre>
            <p>
              La declaración <code>else</code> se utiliza para ejecutar un
              bloque de código si la condición no se cumple:
            </p>
            <pre>
              <code>
                {`if (condicion) {
  // Código a ejecutar si la condición es verdadera
} else {
  // Código a ejecutar si la condición es falsa
}`}
              </code>
            </pre>
          </div>
        </DefaultSlide>
        <DefaultSlide title="If...Else">
          <div>
          <p>
              También se puede utilizar la declaración <code>else if</code> para
              comprobar múltiples condiciones:
            </p>
            <pre>
              <code>
                {`if (condicion1) {
  // Código a ejecutar si la condición1 es verdadera
} else if (condicion2) {
  // Código a ejecutar si la condición2 es verdadera
} else {
  // Código a ejecutar si ninguna de las condiciones es verdadera
}`}
              </code>
            </pre>
            <p>
              En este caso, se comprueba si <code>condicion1</code> es verdadera
              y, si no lo es, se comprueba <code>condicion2</code>.
            </p>
            </div>
        </DefaultSlide>
        <DefaultSlide title="Operador ternario">
          <div>
            <p>
              El operador ternario es una forma abreviada de la declaración{" "}
              <code>if</code>. Su sintaxis es la siguiente:
            </p>
            <pre>
              <code>{`condicion ? valorSiVerdadero : valorSiFalso;`}</code>
            </pre>
            <p>
              En este caso, si la condición es verdadera, se devuelve{" "}
              <code>valorSiVerdadero</code>, de lo contrario se devuelve{" "}
              <code>valorSiFalso</code>.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Switch">
          <div>
            <p>
              La declaración <code>switch</code> se utiliza para ejecutar un
              bloque de código en función del valor de una expresión:
            </p>
            <pre>
              <code>{`switch (expresion) {
  case valor1:
    // Código a ejecutar si la expresión es igual a valor1
    break;
  case valor2:
    // Código a ejecutar si la expresión es igual a valor2
    break;
  default:
    // Código a ejecutar si la expresión no coincide con ningún valor
}`}</code>
            </pre>
          </div>
        </DefaultSlide>
        <DefaultSlide title="While">
          <div>
            <p>
              La declaración <code>while</code> se utiliza para ejecutar un
              bloque de código mientras se cumpla una condición:
            </p>
            <pre>
              <code>
          {`while (condicion) {
  // Código a ejecutar mientras la condición sea verdadera
}`}
              </code>
            </pre>
            <p>
              En este caso, el bloque de código se ejecutará repetidamente
              mientras la condición sea verdadera.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Do..While">
          <div>
            <p>
              La declaración <code>do..while</code> es similar a la declaración <code>while</code>, pero la condición se evalúa después de ejecutar el bloque de código, lo que garantiza que el bloque se ejecute al menos una vez:
            </p>
            <pre>
              <code>
          {`do {
  // Código a ejecutar mientras la condición sea verdadera
} while (condicion);`}
              </code>
            </pre>
            <p>
              En este caso, el bloque de código se ejecutará una vez y luego se repetirá mientras la condición sea verdadera.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="For">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              La declaración <code>for</code> se utiliza para ejecutar un bloque de código un número específico de veces. Su sintaxis es la siguiente:
            </p>
            <pre>
              <code>
          {`for (inicializacion; condicion; actualizacion) {
  // Código a ejecutar en cada iteración
}`}
              </code>
            </pre>
            <p>
              En este caso, se inicializa una variable, se comprueba una condición y se actualiza la variable en cada iteración del bucle.
            </p>
            <p>Ejemplo:</p>
            <pre>
              <code>
          {`for (let i = 0; i < 5; i++) {
  console.log(i); // Imprime los números del 0 al 4
}`}
              </code>
            </pre>
          </div>
        </DefaultSlide>
        <DefaultSlide title="For..in">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              La declaración <code>for..in</code> se utiliza para iterar sobre
              las propiedades enumerables de un objeto. Su sintaxis es la
              siguiente:
            </p>
            <pre>
              <code>
          {`for (let propiedad in objeto) {
  // Código a ejecutar para cada propiedad
}`}
              </code>
            </pre>
            <p>Ejemplo:</p>
            <pre>
              <code>
          {`let persona = { nombre: "Juan", edad: 30, ciudad: "Madrid" };
for (let propiedad in persona) {
  console.log(propiedad + ": " + persona[propiedad]);
  // Imprime "nombre: Juan", "edad: 30", "ciudad: Madrid"
}`}
              </code>
            </pre>
          </div>
        </DefaultSlide>
        <DefaultSlide title="For..of">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              La declaración <code>for..of</code> se utiliza para iterar sobre
              los valores de un objeto iterable (como un array, un string, un
              mapa, etc.). Su sintaxis es la siguiente:
            </p>
            <pre>
              <code>
          {`for (let valor of iterable) {
  // Código a ejecutar para cada valor
}`}
              </code>
            </pre>
            <p>Ejemplo:</p>
            <pre>
              <code>
          {`let numeros = [1, 2, 3, 4, 5];
for (let numero of numeros) {
  console.log(numero); // Imprime los números del 1 al 5
}`}
              </code>
            </pre>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Try..catch">
          <div style={{ fontSize: "0.75em" }}>
            <p style={{ marginTop: -20 }}>
              La declaración <code>try..catch</code> se utiliza para manejar
              excepciones en JavaScript. Permite ejecutar un bloque de código y
              capturar cualquier error que ocurra durante su ejecución:
            </p>
            <pre>
              <code>
          {`try {
  // Código a ejecutar
} catch (error) {
  // Código a ejecutar si ocurre un error
}`}
              </code>
            </pre>
            <p>
              En este caso, el bloque de código dentro de <code>try</code> se
              ejecutará, y si ocurre un error, el bloque de código dentro de{" "}
              <code>catch</code> se ejecutará con el objeto de error.
            </p>
            <p>Ejemplo:</p>
            <pre>
              <code>
          {`try {
  let resultado = 10 / 0;
  console.log(resultado);
} catch (error) {
  console.error("Ocurrió un error:", error);
}`}
              </code>
            </pre>
          </div>
        </DefaultSlide>
      </section>

      <section>
        <TitleSlide title="Ejercicio práctico" />
      </section>

      <ImageSlide image="/images/S03/dancing3.gif" title="¡Fin de la clase!">
        <p>Recuerda completar todos los contenidos de la semana.</p>
        {/* <button className="btn btn-success">Marcar como completo</button> */}
      </ImageSlide>
    </>
  );
}


